// client/src/pages/SeoLandingPage.js — the city landing pages:
//   /list-your-property/:city          "List your property in {city}" (owners)
//   /commercial-space-for-lease/:city  "Commercial space for lease in {city}" (tenants)
// Generated from data (lib/seo.landingFor): only cities with listings or in the service
// area set on the Search & Social Metadata screen. In production the server has already
// put the title, description, canonical and JSON-LD into the HTML (routes/seoPublic.js);
// a page with too few listings is noindex there and here.
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { addressLine, placeLine, photoUrl, onImgError, STATUS_STYLE } from '../utils/properties';
import { IdentityBlock, Disclaimer } from '../components/public/BrokerageIdentity';

const setRobots = (content) => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'robots'; document.head.appendChild(meta); }
    meta.content = content;
};

const SeoLandingPage = ({ kind }) => {
    const { city } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        setData(null); setError('');
        fetch(`/api/public/seo/landing/${kind}/${encodeURIComponent(city)}`)
            .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.message || 'Not found.'); return j; })
            .then((j) => { setData(j); document.title = j.title; setRobots(j.noindex ? 'noindex, follow' : ''); })
            .catch((e) => { setError(e.message); setRobots('noindex, follow'); });
        return () => setRobots('');
    }, [kind, city]);

    if (error) {
        return (
            <div className="mx-auto max-w-3xl px-4 py-16 text-center">
                <h1 className="text-2xl font-bold text-slate-900">{error}</h1>
                <p className="mt-4"><Link to="/properties" className="text-indigo-700 underline">Search all properties</Link></p>
            </div>
        );
    }
    if (!data) return <div className="px-4 py-16 text-center text-slate-600">Loading…</div>;

    const searchLink = kind === 'lease'
        ? `/properties?location=${encodeURIComponent(data.city)}&types=commercial,industrial&status=for_rent`
        : `/properties?location=${encodeURIComponent(data.city)}`;
    return (
        <div className="mx-auto max-w-6xl px-4 py-10">
            <nav aria-label="Breadcrumb" className="text-sm text-slate-600">
                <Link to="/" className="hover:underline">Home</Link> ›{' '}
                {kind === 'list' ? <Link to="/list-your-property" className="hover:underline">List your property</Link> : <Link to="/properties" className="hover:underline">Properties</Link>} ›{' '}
                <span aria-current="page">{data.city}</span>
            </nav>
            <h1 className="mt-3 text-3xl font-extrabold text-slate-900 md:text-4xl">{data.h1}</h1>
            <p className="mt-4 max-w-3xl text-lg text-slate-700">{data.intro}</p>
            <div className="mt-6 flex flex-wrap gap-3">
                {kind === 'list' ? (
                    <Link to="/list-your-property" className="rounded-md bg-amber-400 px-6 py-3 text-lg font-extrabold uppercase tracking-wide text-slate-900 shadow-md ring-2 ring-amber-500 hover:bg-amber-300">
                        List your property
                    </Link>
                ) : (
                    <Link to="/contact" className="rounded-md bg-slate-900 px-6 py-3 text-lg font-bold text-white shadow-md hover:bg-slate-900">
                        Tell us what space you need
                    </Link>
                )}
                {data.count > 0 && (
                    <Link to={searchLink} className="rounded-md border border-slate-400 px-6 py-3 text-lg font-semibold text-slate-900 hover:bg-slate-100">
                        See all {data.count} in {data.city}
                    </Link>
                )}
            </div>

            {data.listings.length > 0 && (
                <section className="mt-10" aria-labelledby="recent-h">
                    <h2 id="recent-h" className="text-2xl font-bold text-slate-900">
                        {kind === 'lease' ? `Spaces for lease in ${data.city}` : `On the market in ${data.city}`}
                    </h2>
                    <ul className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {data.listings.map((l) => (
                            <li key={l.key} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                                <Link to={l.path} className="block hover:bg-slate-50">
                                    <img src={photoUrl(l)} onError={onImgError} alt={(l.photos && l.photos[0] && (l.photos[0].alt || l.photos[0].caption)) || `${l.type_label}, ${addressLine(l)}`}
                                        className="h-44 w-full object-cover" loading="lazy" />
                                    <div className="p-4">
                                        <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[l.status] || ''}`}>{l.status_label}</span>
                                        <p className="mt-1 text-lg font-bold text-slate-900">{l.price_label}</p>
                                        <p className="text-slate-800">{addressLine(l)}</p>
                                        <p className="text-sm text-slate-600">{placeLine(l)} · {l.type_label}</p>
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {data.others.length > 0 && (
                <section className="mt-10" aria-labelledby="other-h">
                    <h2 id="other-h" className="text-xl font-bold text-slate-900">Nearby areas we serve</h2>
                    <ul className="mt-3 flex flex-wrap gap-2">
                        {data.others.map((o) => (
                            <li key={o.path}><Link to={o.path} className="inline-block rounded-full border border-slate-300 px-3 py-1 text-sm text-slate-800 hover:bg-slate-100">{o.city}</Link></li>
                        ))}
                    </ul>
                </section>
            )}

            <div className="mt-10 space-y-3">
                {data.identity && <IdentityBlock identity={data.identity} />}
                {data.disclaimer && <Disclaimer text={data.disclaimer} />}
            </div>
        </div>
    );
};

export default SeoLandingPage;
